/**
 * AI Studio — 5-step content generator for one calendar activity.
 *
 * Flow:
 *   1. Review the brief + client-supplied reference assets (read-only).
 *   2. Generate hero image (Gemini Nano Banana). Cost is previewed BEFORE
 *      the user spends credits.
 *   3. (Optional) Generate a short Seedance video. Same cost preview gate.
 *   4. Generate post copy from Claude + OpenAI + Gemini in parallel —
 *      shows three cards side-by-side, user picks one and can edit it.
 *   5. Schedule + finalise. Writes back to the activity record.
 *
 * Access: admins always pass. Client contacts only if their user record
 * has `can_use_ai_studio: true` (set by the admin on the Contacts page).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Wand2, Image as ImageIcon, Film, FileText, CalendarClock, Sparkles,
  ChevronLeft, ChevronRight, Check, Loader2, AlertCircle, Bot, Coins, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "brief",    label: "Review brief",  Icon: FileText },
  { key: "image",    label: "Hero image",    Icon: ImageIcon },
  { key: "video",    label: "Video (opt.)",  Icon: Film },
  { key: "copy",     label: "Post copy",     Icon: Sparkles },
  { key: "schedule", label: "Schedule",      Icon: CalendarClock },
];

const IMAGE_PROVIDERS = [
  { value: "nano_banana", label: "Gemini Nano Banana" },
  { value: "gpt_image_1", label: "OpenAI GPT-Image-1" },
];

const ASPECT_OPTIONS = [
  { value: "1:1",  label: "Square (1:1)" },
  { value: "4:5",  label: "Portrait (4:5)" },
  { value: "9:16", label: "Reel / Story (9:16)" },
  { value: "16:9", label: "Landscape (16:9)" },
];

const fmtCost = (n) =>
  n == null ? "—"
  : n < 0.01 ? `$${n.toFixed(4)}`
  : `$${n.toFixed(3)}`;

// Build a displayable URL for an activity file. Uploaded references are
// served through the authenticated calendar files endpoint (cookie auth);
// AI-generated assets carry their own `url`.
const fileSrc = (activityId, f) => {
  if (f?.file_id && !f?.source) {
    return `${API}/calendar/activities/${activityId}/files/${f.file_id}`;
  }
  if (f?.url) {
    // Legacy AI-studio URLs pointed at an unmounted /uploads path — rewrite
    // them onto the authenticated asset route.
    let u = f.url;
    const m = u.match(/^\/uploads\/ai_studio\/([^/]+)\/(.+)$/);
    if (m) u = `/ai-studio/assets/${m[1]}/${m[2]}`;
    return u.startsWith("http") ? u : `${API}${u}`;
  }
  if (f?.file_id) return `${API}/calendar/activities/${activityId}/files/${f.file_id}`;
  return null;
};

export default function AIStudioPage() {
  const { clientId, activityId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const [access, setAccess] = useState({ loading: true });

  // Step-local state
  const [imageAspect, setImageAspect] = useState("1:1");
  const [imageProvider, setImageProvider] = useState("nano_banana");
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageResults, setImageResults] = useState([]);
  const [imageBusy, setImageBusy] = useState(false);
  // When set, the next generation EDITS this uploaded photo instead of
  // creating from scratch.
  const [editBase, setEditBase] = useState(null);
  // Creative Library asset_ids selected as visual reference context.
  const [libRefIds, setLibRefIds] = useState([]);

  const [videoAspect, setVideoAspect] = useState("16:9");
  const [videoDuration, setVideoDuration] = useState(5);
  const [videoPrompt, setVideoPrompt] = useState("");
  const [videoJob, setVideoJob] = useState(null);
  const [videoBusy, setVideoBusy] = useState(false);

  const [copyExtras, setCopyExtras] = useState("");
  const [copyResults, setCopyResults] = useState([]);
  const [selectedCopyKey, setSelectedCopyKey] = useState(null);
  const [editedCopy, setEditedCopy] = useState("");
  const [copyBusy, setCopyBusy] = useState(false);

  const [scheduledAt, setScheduledAt] = useState("");
  const [autoPost, setAutoPost] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  // Cost-preview modal (gates every paid action)
  const [costPreview, setCostPreview] = useState(null); // {data, onConfirm}

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/ai-studio/access/${clientId}`),
      api.get(`/ai-studio/activity/${activityId}`),
    ])
      .then(([accRes, bRes]) => {
        if (cancelled) return;
        setAccess(accRes.data);
        setBundle(bRes.data);
        // Pre-fill prompts from the brief (the calendar dialog saves it as
        // `description`; older records may use `brief`).
        const act = bRes.data?.activity || {};
        const brief = act.brief || act.description || act.title || "";
        setImagePrompt(brief.slice(0, 800));
        setVideoPrompt(brief.slice(0, 600));
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || "Couldn't load this activity");
        setAccess({ ok: false, reason: "load_failed" });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [clientId, activityId]);

  // Poll the video job until it lands.
  useEffect(() => {
    if (!videoJob?.task_id || videoJob?.event?.status === "succeeded") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/ai-studio/video/${videoJob.task_id}`);
        setVideoJob(data);
        if (data?.event?.status === "succeeded" || data?.event?.status === "failed") {
          clearInterval(t);
        }
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [videoJob?.task_id]);

  const requestEstimate = async (action, extra = {}) => {
    const body = { action, activity_id: activityId, ...extra };
    if (action === "copy") body.prompt_chars = (copyExtras + (bundle?.context_md || "")).length || 1500;
    if (action === "image") {
      body.aspect_ratio = imageAspect;
      body.image_provider = imageProvider;
    }
    if (action === "video") {
      body.aspect_ratio = videoAspect;
      body.duration_seconds = videoDuration;
    }
    const { data } = await api.post(`/ai-studio/estimate`, body);
    return data;
  };

  const confirmCost = (estimateData, onConfirm) => {
    setCostPreview({ data: estimateData, onConfirm });
  };

  const generateImage = async () => {
    try {
      const est = await requestEstimate("image");
      confirmCost(est, async () => {
        setCostPreview(null);
        setImageBusy(true);
        try {
          // Generation takes 1-2 min — far beyond proxy timeouts — so the
          // backend runs it as a job and we poll for the result.
          const { data: job } = await api.post(`/ai-studio/image`, {
            activity_id: activityId,
            prompt: imagePrompt,
            aspect_ratio: imageAspect,
            provider: imageProvider,
            use_reference_assets: true,
            base_file_id: editBase?.file_id || undefined,
            base_asset_id: editBase?.asset_id || undefined,
            library_asset_ids: libRefIds,
          });
          let finished = false;
          for (let i = 0; i < 100; i++) {
            await new Promise((r) => setTimeout(r, 3000));
            let j;
            try {
              ({ data: j } = await api.get(`/ai-studio/image-jobs/${job.job_id}`));
            } catch { continue; }
            if (j.status === "done") {
              setImageResults((prev) => [...(j.images || []), ...prev]);
              toast.success(`Generated ${j.images?.length || 0} image(s) · ${fmtCost(j.cost_usd)}`);
              setEditBase(null);
              finished = true;
              break;
            }
            if (j.status === "failed") {
              toast.error(j.error || "Image generation failed");
              finished = true;
              break;
            }
          }
          if (!finished) toast.error("Still rendering after 5 minutes — try again shortly.");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Image generation failed");
        } finally {
          setImageBusy(false);
        }
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't estimate cost");
    }
  };

  const useUploadedAsHero = (f) => {
    const url = `/calendar/activities/${activityId}/files/${f.file_id}`;
    setImageResults((prev) => [
      { url, filename: f.filename, model: "uploaded photo", provider: "upload", file_id: f.file_id },
      ...prev.filter((r) => r.file_id !== f.file_id),
    ]);
    setEditBase(null);
    toast.success(`"${f.filename}" set as hero — move to the next step, or describe AI edits below.`);
  };

  const submitVideo = async () => {
    try {
      const est = await requestEstimate("video");
      confirmCost(est, async () => {
        setCostPreview(null);
        setVideoBusy(true);
        try {
          const { data } = await api.post(`/ai-studio/video`, {
            activity_id: activityId,
            prompt: videoPrompt,
            aspect_ratio: videoAspect,
            duration_seconds: videoDuration,
          });
          setVideoJob({ job: data, event: null });
          toast.info("Seedance job submitted — we'll poll for the result.");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Video submission failed");
        } finally {
          setVideoBusy(false);
        }
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't estimate cost");
    }
  };

  const generateCopy = async () => {
    try {
      const est = await requestEstimate("copy");
      confirmCost(est, async () => {
        setCostPreview(null);
        setCopyBusy(true);
        try {
          // Runs as a background job — polled — so slow model responses
          // can't hit proxy timeouts.
          const { data: job } = await api.post(`/ai-studio/copy`, {
            activity_id: activityId,
            providers: ["claude", "openai", "gemini"],
            extra_instructions: copyExtras || undefined,
          });
          let finished = false;
          for (let i = 0; i < 100; i++) {
            await new Promise((r) => setTimeout(r, 3000));
            let j;
            try {
              ({ data: j } = await api.get(`/ai-studio/copy-jobs/${job.job_id}`));
            } catch { continue; }
            if (j.status === "done") {
              setCopyResults(j.results || []);
              const firstOk = (j.results || []).find((r) => r.ok);
              if (firstOk) {
                setSelectedCopyKey(firstOk.provider);
                setEditedCopy(firstOk.text || "");
              }
              finished = true;
              break;
            }
            if (j.status === "failed") {
              toast.error(j.error || "Copy generation failed");
              finished = true;
              break;
            }
          }
          if (!finished) toast.error("Still writing after 5 minutes — try again shortly.");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Copy generation failed");
        } finally {
          setCopyBusy(false);
        }
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't estimate cost");
    }
  };

  const finalize = async () => {
    setFinalizing(true);
    try {
      const body = { activity_id: activityId };
      if (editedCopy) body.post_copy = editedCopy;
      if (imageResults.length) {
        body.image_paths = imageResults.map((i) => i.url);
      }
      if (videoJob?.event?.video_url) body.video_url = videoJob.event.video_url;
      if (scheduledAt) body.scheduled_at = new Date(scheduledAt).toISOString();
      body.auto_post = autoPost;
      await api.post(`/ai-studio/finalize`, body);
      toast.success("Saved to calendar activity");
      navigate(`/clients/${clientId}/calendar`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Finalise failed");
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center gap-2 text-sm text-zinc-500" data-testid="ai-studio-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading AI Studio…
      </div>
    );
  }

  if (!access?.ok) {
    return (
      <div className="p-10 max-w-xl">
        <Card className="rounded-sm border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <h2 className="font-display font-bold text-xl tracking-tight">
              AI Studio isn&apos;t enabled for your account yet
            </h2>
            <p className="text-sm text-zinc-700 mt-2">
              {access?.hint || "Ask an Iceberg Digital admin to grant you AI Studio + Calendar editor permissions on your contact profile."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const activity = bundle?.activity || {};
  const client = bundle?.client || {};
  const step = STEPS[stepIdx];

  return (
    <div className="p-6 lg:p-8 max-w-6xl space-y-6" data-testid="ai-studio-page">
      <header className="space-y-3">
        <div className="flex items-center gap-2 text-xs label-overline text-[#002FA7]">
          <Wand2 className="h-3 w-3" /> AI Studio
        </div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight">
          {activity.title || "Untitled activity"}
        </h1>
        <p className="text-sm text-zinc-500">
          {client.company_name} · {activity.platform || "—"} ·{" "}
          {activity.content_type || "post"}
          {activity.start_time && (
            <> · scheduled for{" "}
              <strong>{new Date(activity.start_time).toLocaleString()}</strong>
            </>
          )}
        </p>
      </header>

      {/* Step rail */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-3 flex items-center gap-1 overflow-x-auto">
          {STEPS.map((s, i) => {
            const done = i < stepIdx;
            const active = i === stepIdx;
            const Icon = s.Icon;
            return (
              <button key={s.key}
                type="button"
                onClick={() => setStepIdx(i)}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs rounded-sm whitespace-nowrap
                  ${active ? "bg-[#002FA7] text-white"
                          : done ? "bg-emerald-50 text-emerald-700"
                                 : "text-zinc-500 hover:bg-zinc-50"}`}
                data-testid={`step-${s.key}`}>
                {done ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                <span className="font-bold">{i + 1}.</span> {s.label}
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Step body */}
      {step.key === "brief" && (
        <BriefStep activity={activity} client={client} contextMd={bundle?.context_md} />
      )}

      {step.key === "image" && (
        <ImageStep
          prompt={imagePrompt} setPrompt={setImagePrompt}
          aspect={imageAspect} setAspect={setImageAspect}
          provider={imageProvider} setProvider={setImageProvider}
          results={imageResults} busy={imageBusy}
          onGenerate={generateImage}
          activityId={activity.activity_id}
          editBase={editBase} setEditBase={setEditBase}
          onUseAsHero={useUploadedAsHero}
          onNext={() => setStepIdx(stepIdx + 1)}
          libraryAssets={bundle?.library_assets || []}
          libRefIds={libRefIds} setLibRefIds={setLibRefIds}
          referenceFiles={(activity.files || []).filter(
            (f) => (f.mime || "").startsWith("image/"))} />
      )}

      {step.key === "video" && (
        <VideoStep
          prompt={videoPrompt} setPrompt={setVideoPrompt}
          aspect={videoAspect} setAspect={setVideoAspect}
          duration={videoDuration} setDuration={setVideoDuration}
          job={videoJob} busy={videoBusy} onSubmit={submitVideo}
          onSkip={() => setStepIdx(stepIdx + 1)} />
      )}

      {step.key === "copy" && (
        <CopyStep
          extras={copyExtras} setExtras={setCopyExtras}
          results={copyResults} busy={copyBusy}
          onGenerate={generateCopy}
          selected={selectedCopyKey} setSelected={setSelectedCopyKey}
          edited={editedCopy} setEdited={setEditedCopy} />
      )}

      {step.key === "schedule" && (
        <ScheduleStep
          activity={activity}
          scheduledAt={scheduledAt} setScheduledAt={setScheduledAt}
          autoPost={autoPost} setAutoPost={setAutoPost}
          postCopy={editedCopy} images={imageResults}
          video={videoJob?.event?.video_url}
          busy={finalizing} onFinalize={finalize} />
      )}

      {/* Step nav */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" className="rounded-sm"
          disabled={stepIdx === 0}
          onClick={() => setStepIdx(Math.max(0, stepIdx - 1))}
          data-testid="step-prev">
          <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back
        </Button>
        {stepIdx < STEPS.length - 1 ? (
          <Button className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
            onClick={() => setStepIdx(Math.min(STEPS.length - 1, stepIdx + 1))}
            data-testid="step-next">
            Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        ) : (
          <Button className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={finalizing}
            onClick={finalize}
            data-testid="step-finalize">
            {finalizing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
            Save to calendar
          </Button>
        )}
      </div>

      {/* Cost-preview modal — gates every paid action */}
      <CostPreviewDialog preview={costPreview}
        onCancel={() => setCostPreview(null)}
        onConfirm={() => costPreview?.onConfirm?.()} />
    </div>
  );
}


// ── Step components ───────────────────────────────────────────────────────
function FilePreviewTile({ f, activityId }) {
  const mime = f.mime || "";
  const src = fileSrc(activityId, f);
  return (
    <div className="rounded-sm border border-zinc-200 p-2 text-xs"
      data-testid={`brief-file-${f.file_id || f.filename}`}>
      {mime.startsWith("image/") && src ? (
        <a href={src} target="_blank" rel="noreferrer">
          <img src={src} alt={f.filename}
            className="w-full h-24 object-cover rounded-sm" />
        </a>
      ) : mime.startsWith("audio/") && src ? (
        <div className="h-24 flex flex-col items-center justify-center gap-2 bg-zinc-50 rounded-sm px-1">
          <Film className="h-5 w-5 text-zinc-400 rotate-90" />
          <audio src={src} controls className="w-full h-8" />
        </div>
      ) : mime.startsWith("video/") && src ? (
        <video src={src} controls className="w-full h-24 object-cover rounded-sm bg-black" />
      ) : (
        <a href={src || "#"} target="_blank" rel="noreferrer"
          className="h-24 grid place-items-center text-zinc-400 bg-zinc-50 rounded-sm hover:text-zinc-600">
          {mime || "file"}
        </a>
      )}
      <div className="mt-1 truncate" title={f.filename}>{f.filename}</div>
    </div>
  );
}

function BriefStep({ activity, client, contextMd }) {
  const files = activity.files || [];
  const links = activity.reference_links || [];
  const briefText = activity.brief || activity.description;
  return (
    <Card className="rounded-sm" data-testid="step-body-brief">
      <CardContent className="p-5 space-y-4">
        <div>
          <Label className="label-overline">Brief</Label>
          <p className="text-sm text-zinc-800 whitespace-pre-wrap mt-1" data-testid="brief-text">
            {briefText || <span className="text-zinc-400 italic">No brief written for this activity yet.</span>}
          </p>
        </div>
        {!!links.length && (
          <div>
            <Label className="label-overline">Reference links ({links.length})</Label>
            <ul className="text-sm space-y-1 mt-1">
              {links.map((l, i) => (
                <li key={l.url || `link-${i}`}><a href={l.url} target="_blank" rel="noreferrer"
                  className="text-[#002FA7] hover:underline">{l.label || l.url}</a></li>
              ))}
            </ul>
          </div>
        )}
        {!!files.length && (
          <div>
            <Label className="label-overline">Attached assets ({files.length})</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
              {files.map((f, i) => (
                <FilePreviewTile key={f.file_id || f.url || f.filename || `file-${i}`}
                  f={f} activityId={activity.activity_id} />
              ))}
            </div>
          </div>
        )}
        <p className="text-[11px] text-zinc-500 pt-2 border-t border-zinc-100">
          {client.company_name || "This client"}&apos;s brand context is automatically attached to every AI step below.
        </p>
      </CardContent>
    </Card>
  );
}

function ImageStep({ prompt, setPrompt, aspect, setAspect, provider, setProvider, results, busy, onGenerate, referenceFiles, activityId, editBase, setEditBase, onUseAsHero, onNext, libraryAssets, libRefIds, setLibRefIds }) {
  const heroFileIds = new Set(results.filter((r) => r.provider === "upload").map((r) => r.file_id));
  return (
    <Card className="rounded-sm" data-testid="step-body-image">
      <CardContent className="p-5 space-y-4">
        {/* Use an uploaded photo directly — no AI spend needed */}
        {!!referenceFiles.length && (
          <div className="rounded-sm border border-zinc-200 bg-zinc-50/60 p-3 space-y-2"
            data-testid="uploaded-hero-panel">
            <div className="text-xs">
              <span className="font-bold">Already have the shot?</span>{" "}
              Use one of the {referenceFiles.length} uploaded photo{referenceFiles.length === 1 ? "" : "s"} as
              the hero image (free), optionally let the AI edit it — or skip straight to the next step.
            </div>
            <div className="flex flex-wrap gap-3">
              {referenceFiles.map((f, i) => {
                const src = fileSrc(activityId, f);
                const isHero = heroFileIds.has(f.file_id);
                const isEditing = editBase?.file_id === f.file_id;
                return (
                  <div key={f.file_id || f.filename || `up-${i}`}
                    className={`w-32 rounded-sm border bg-white p-1.5 space-y-1.5
                      ${isHero ? "border-emerald-400 ring-1 ring-emerald-300"
                        : isEditing ? "border-[#002FA7] ring-1 ring-[#002FA7]/40"
                        : "border-zinc-200"}`}>
                    {src && <img src={src} alt={f.filename} title={f.filename}
                      className="h-20 w-full object-cover rounded-sm" />}
                    <div className="text-[10px] truncate" title={f.filename}>{f.filename}</div>
                    <div className="flex flex-col gap-1">
                      <Button size="sm" variant={isHero ? "default" : "outline"}
                        className={`rounded-sm h-6 text-[10px] px-1.5 ${isHero ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                        onClick={() => onUseAsHero(f)}
                        data-testid={`use-as-hero-${f.file_id}`}>
                        <Check className="h-3 w-3 mr-1" />{isHero ? "Hero ✓" : "Use as hero"}
                      </Button>
                      <Button size="sm" variant="outline"
                        className={`rounded-sm h-6 text-[10px] px-1.5 ${isEditing ? "border-[#002FA7] text-[#002FA7]" : ""}`}
                        onClick={() => setEditBase(isEditing ? null : f)}
                        data-testid={`edit-with-ai-${f.file_id}`}>
                        <Wand2 className="h-3 w-3 mr-1" />{isEditing ? "Cancel edit" : "Edit with AI"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            {!!heroFileIds.size && !editBase && (
              <Button size="sm" variant="ghost" className="rounded-sm text-xs text-emerald-700"
                onClick={onNext} data-testid="hero-continue-next">
                Hero image chosen — continue to next step →
              </Button>
            )}
          </div>
        )}

        {/* Creative Library — client-approved assets as reference or edit base */}
        {!!libraryAssets.length && (
          <div className="rounded-sm border border-zinc-200 bg-zinc-50/60 p-3 space-y-2"
            data-testid="library-assets-panel">
            <div className="text-xs">
              <span className="font-bold">Creative Library</span>{" "}
              — {libraryAssets.length} client asset{libraryAssets.length === 1 ? "" : "s"}.
              Toggle any as an AI <b>reference</b>, or pick one to <b>edit with AI</b>.
            </div>
            <div className="flex flex-wrap gap-3">
              {libraryAssets.map((a) => {
                const src = `${API}/creative/assets/${a.asset_id}/file`;
                const isRef = libRefIds.includes(a.asset_id);
                const isEditing = editBase?.asset_id === a.asset_id;
                return (
                  <div key={a.asset_id}
                    className={`w-32 rounded-sm border bg-white p-1.5 space-y-1.5
                      ${isEditing ? "border-[#002FA7] ring-1 ring-[#002FA7]/40"
                        : isRef ? "border-emerald-400 ring-1 ring-emerald-300"
                        : "border-zinc-200"}`}>
                    <img src={src} alt={a.name} title={a.name}
                      className="h-20 w-full object-cover rounded-sm" />
                    <div className="text-[10px] truncate" title={a.name}>{a.name || a.filename}</div>
                    <div className="flex flex-col gap-1">
                      <Button size="sm" variant={isRef ? "default" : "outline"}
                        className={`rounded-sm h-6 text-[10px] px-1.5 ${isRef ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                        onClick={() => setLibRefIds((ids) =>
                          isRef ? ids.filter((x) => x !== a.asset_id) : [...ids, a.asset_id])}
                        data-testid={`lib-ref-toggle-${a.asset_id}`}>
                        <Check className="h-3 w-3 mr-1" />{isRef ? "Reference ✓" : "Use as reference"}
                      </Button>
                      <Button size="sm" variant="outline"
                        className={`rounded-sm h-6 text-[10px] px-1.5 ${isEditing ? "border-[#002FA7] text-[#002FA7]" : ""}`}
                        onClick={() => setEditBase(isEditing ? null
                          : { asset_id: a.asset_id, filename: a.name || a.filename })}
                        data-testid={`lib-edit-with-ai-${a.asset_id}`}>
                        <Wand2 className="h-3 w-3 mr-1" />{isEditing ? "Cancel edit" : "Edit with AI"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {editBase && (
          <div className="rounded-sm bg-[#002FA7]/[0.05] border border-[#002FA7]/30 p-3 text-xs flex items-center gap-2"
            data-testid="edit-mode-banner">
            <Wand2 className="h-4 w-4 text-[#002FA7] flex-shrink-0" />
            <span>
              <strong>Editing “{editBase.filename}”</strong> — describe the changes below
              (e.g. “remove the car in the background”, “make it dusk lighting”, “add our logo top-right”).
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="label-overline">{editBase ? "Edit instructions" : "Image prompt"}</Label>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
              rows={4} className="rounded-sm mt-1"
              data-testid="image-prompt"
              placeholder={editBase
                ? "Describe the changes to make to the selected photo…"
                : "Describe what the image should show — composition, mood, subject, brand cues…"} />
          </div>
          <div className="space-y-3">
            <div>
              <Label className="label-overline">Provider</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="image-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMAGE_PROVIDERS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-overline">Aspect ratio</Label>
              <Select value={aspect} onValueChange={setAspect}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="image-aspect">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASPECT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {!editBase && !!referenceFiles.length && (
          <div className="rounded-sm bg-zinc-50 p-3 text-xs space-y-2">
            <div>
              <span className="font-bold">{referenceFiles.length} reference image{referenceFiles.length === 1 ? "" : "s"}</span>{" "}
              from the activity will be passed as visual context.
            </div>
            <div className="flex flex-wrap gap-2">
              {referenceFiles.map((f, i) => {
                const src = fileSrc(activityId, f);
                return src ? (
                  <img key={f.file_id || f.filename || `ref-${i}`} src={src} alt={f.filename}
                    title={f.filename}
                    className="h-14 w-14 object-cover rounded-sm border border-zinc-200" />
                ) : null;
              })}
            </div>
          </div>
        )}

        <Button onClick={onGenerate} disabled={busy || prompt.length < 4}
          className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
          data-testid="image-generate">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
          {busy ? "Generating — this can take a minute or two…"
            : editBase ? "Generate edited image" : "Generate image"}
        </Button>

        {!!results.length && (
          <div>
            <Label className="label-overline">Generated ({results.length})</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
              {results.map((img, i) => (
                <a key={img.url || `img-${i}`} href={img.url.startsWith("http") ? img.url : `${API}${img.url}`}
                  target="_blank" rel="noreferrer"
                  className="block rounded-sm border border-zinc-200 hover:border-[#002FA7] overflow-hidden">
                  <img src={img.url.startsWith("http") ? img.url : `${API}${img.url}`}
                    alt="" className="w-full h-40 object-cover" />
                  <div className="p-1.5 text-[10px] text-zinc-500 font-mono truncate">
                    {img.model}
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function VideoStep({ prompt, setPrompt, aspect, setAspect, duration, setDuration, job, busy, onSubmit, onSkip }) {
  const event = job?.event;
  return (
    <Card className="rounded-sm" data-testid="step-body-video">
      <CardContent className="p-5 space-y-4">
        <div className="rounded-sm bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>Video is optional — Seedance jobs cost more than images. Skip this step if a still works.</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="label-overline">Video prompt</Label>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
              rows={3} className="rounded-sm mt-1" data-testid="video-prompt"
              placeholder="Describe the motion / action / setting…" />
          </div>
          <div className="space-y-2">
            <div>
              <Label className="label-overline">Aspect</Label>
              <Select value={aspect} onValueChange={setAspect}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="video-aspect">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASPECT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-overline">Duration (sec)</Label>
              <Input type="number" min={4} max={15} value={duration}
                onChange={(e) => setDuration(Math.max(4, Math.min(15, Number(e.target.value) || 5)))}
                className="rounded-sm mt-1" data-testid="video-duration" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={onSubmit} disabled={busy || prompt.length < 4}
            className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
            data-testid="video-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Film className="h-4 w-4 mr-1.5" />}
            Submit video job
          </Button>
          <Button onClick={onSkip} variant="ghost" className="rounded-sm text-xs"
            data-testid="video-skip">Skip video step →</Button>
        </div>

        {job && (
          <div className="rounded-sm border border-zinc-200 p-3 text-xs">
            <div className="font-bold mb-1">Job {job.job?.task_id}</div>
            <div className="text-zinc-500">Status: {event?.status || "submitted (rendering)"}</div>
            {event?.video_url && (
              <video src={event.video_url} controls className="w-full mt-2 rounded-sm" />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CopyStep({ extras, setExtras, results, busy, onGenerate, selected, setSelected, edited, setEdited }) {
  const selectedRow = results.find((r) => r.provider === selected);
  return (
    <Card className="rounded-sm" data-testid="step-body-copy">
      <CardContent className="p-5 space-y-4">
        <div>
          <Label className="label-overline">Extra instructions (optional)</Label>
          <Textarea value={extras} onChange={(e) => setExtras(e.target.value)}
            rows={2} className="rounded-sm mt-1" data-testid="copy-extras"
            placeholder="e.g. 'Lead with the regional FM angle', 'No emojis', '2 sentences max'…" />
        </div>
        <Button onClick={onGenerate} disabled={busy}
          className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
          data-testid="copy-generate">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Bot className="h-4 w-4 mr-1.5" />}
          {busy ? "Asking Claude, OpenAI and Gemini…" : "Race 3 AIs for the best copy"}
        </Button>

        {!!results.length && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {results.map((r) => (
                <button key={r.provider}
                  type="button"
                  onClick={() => {
                    if (!r.ok) return;
                    setSelected(r.provider);
                    setEdited(r.text || "");
                  }}
                  className={`text-left rounded-sm border p-3 transition-colors
                    ${selected === r.provider ? "border-[#002FA7] bg-[#002FA7]/[0.04]" : "border-zinc-200 hover:border-zinc-300"}
                    ${!r.ok ? "opacity-60" : ""}`}
                  data-testid={`copy-card-${r.provider}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold">{r.label}</span>
                    <Badge variant="outline" className="rounded-sm font-mono text-[10px]">
                      {fmtCost(r.cost_usd)}
                    </Badge>
                  </div>
                  {r.ok ? (
                    <p className="text-xs text-zinc-700 whitespace-pre-wrap line-clamp-[14]">
                      {r.text}
                    </p>
                  ) : (
                    <p className="text-xs text-red-600">{r.error || "Failed"}</p>
                  )}
                </button>
              ))}
            </div>

            {selectedRow?.ok && (
              <div>
                <Label className="label-overline">
                  Edit the chosen draft ({selectedRow.label})
                </Label>
                <Textarea value={edited} onChange={(e) => setEdited(e.target.value)}
                  rows={8} className="rounded-sm mt-1 font-sans"
                  data-testid="copy-final-edit" />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ScheduleStep({ activity, scheduledAt, setScheduledAt, autoPost, setAutoPost, postCopy, images, video, busy, onFinalize }) {
  return (
    <Card className="rounded-sm" data-testid="step-body-schedule">
      <CardContent className="p-5 space-y-4">
        <div className="rounded-sm bg-[#002FA7]/[0.04] border border-[#002FA7]/20 p-3 text-xs">
          <strong>Auto-posting infrastructure is not built yet.</strong>{" "}
          For now this just stamps the scheduled date on the activity — the
          publisher will be wired in a follow-up phase. Your team can still
          manually post on the date you pick.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="label-overline">Scheduled date / time</Label>
            <Input type="datetime-local" value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="rounded-sm mt-1" data-testid="schedule-at" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={autoPost}
                onChange={(e) => setAutoPost(e.target.checked)}
                className="h-4 w-4 accent-[#002FA7]"
                data-testid="schedule-autopost" />
              Queue for auto-posting (when publisher is live)
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="label-overline">Preview</Label>
          <div className="rounded-sm border border-zinc-200 p-3 space-y-3">
            <div className="text-xs text-zinc-500">
              {activity.platform || "post"} · {activity.content_type || "—"}
            </div>
            {images?.[0] && (
              <img src={images[0].url.startsWith("http") ? images[0].url : `${API}${images[0].url}`}
                alt="" className="w-full max-h-72 object-cover rounded-sm" />
            )}
            {video && <video src={video} controls className="w-full rounded-sm" />}
            <p className="text-sm whitespace-pre-wrap">
              {postCopy || <span className="text-zinc-400 italic">No copy selected — go back to the copy step.</span>}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


// ── Cost preview modal — fired BEFORE every paid action ────────────────────
function CostPreviewDialog({ preview, onCancel, onConfirm }) {
  const data = preview?.data;
  if (!data) return null;
  return (
    <Dialog open={!!preview} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="cost-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-4 w-4 text-amber-600" />
            Confirm AI action · {data.action}
          </DialogTitle>
          <DialogDescription>{data.explanation}</DialogDescription>
        </DialogHeader>
        {data.action === "copy" ? (
          <div className="space-y-2">
            {data.providers.map((p) => (
              <div key={p.key} className="flex items-center justify-between text-xs font-mono">
                <span>{p.label} ({p.model})</span>
                <span>{p.input_tokens} in + {p.output_tokens} out · {fmtCost(p.cost_usd)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-zinc-200 text-sm font-bold">
              <span>Total estimate</span>
              <span>{fmtCost(data.total_cost_usd)}</span>
            </div>
          </div>
        ) : (
          <div className="text-sm space-y-1">
            <div><strong>Model:</strong> {data.model}</div>
            <div><strong>Units:</strong> {data.units}</div>
            <div className="flex items-center justify-between pt-2 border-t border-zinc-200 font-bold">
              <span>Estimated cost</span>
              <span>{fmtCost(data.cost_usd)}</span>
            </div>
          </div>
        )}
        <p className="text-[11px] text-zinc-500 mt-1">Billed to: {data.billed_to}</p>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onCancel}
            data-testid="cost-cancel">Cancel</Button>
          <Button className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
            onClick={onConfirm} data-testid="cost-confirm">
            <Sparkles className="h-3.5 w-3.5 mr-1" />
            Confirm & spend {fmtCost(data.total_cost_usd ?? data.cost_usd)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
